import { ticketsApi } from "#/api/ticketsApi";
import Popover from "#/components/overlays/Popover";
import { TaskType } from "#/types/tickets";
import { sanitize } from "#/utils/StringUtils";
import { Button, Select, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconFilter, IconPlus } from "@tabler/icons-react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import CreateTicketDrawer from "../items/components/CreateTicketDrawer";
import { KanbanBoard } from "./components/KanbanBoard";

const TicketsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filterBy, seFilterBy] = useState<string>();
  const [
    createTicketDrawerOpened,
    { open: openCreateTicketDrawer, close: closeCreateTicketDrawer },
  ] = useDisclosure(false);

  const {
    data: { items = [] } = {},
    isLoading,
    isPending,
  } = useQuery({
    queryKey: ["items", filterBy],
    queryFn: async () => {
      try {
        const response = await ticketsApi.getAll({
          limit: 100000,
          ...(filterBy ? { filter_by: filterBy } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No items returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,

    retry: false,
  });

  return (
    <div className="w-full h-full flex flex-col gap-6">
      <div className="w-full flex items-center justify-between">
        <Title order={2}>{t("tickets.title")}</Title>
        <div className="flex items-center gap-2">
          <Button
            leftSection={<IconPlus size={18} />}
            variant="filled"
            color="black"
            size="sm"
            onClick={openCreateTicketDrawer}
          >
            {t("tickets.createTicket")}
          </Button>
          <Popover
            anchor={
              <Button
                leftSection={<IconFilter size={18} />}
                variant={filterBy ? "filled" : "outline"}
                color="black"
                size="sm"
                loading={isPending}
              >
                {t("common.filters")}
              </Button>
            }
          >
            <Select
              label={t("common.status")}
              description={t("tickets.filterByStatus")}
              placeholder={t("tickets.selectFilter")}
              data={Object.values(TaskType).map((item) => ({
                label: sanitize(item),
                value: item,
              }))}
              classNames={{
                option: "capitalize",
              }}
              // defaultValue={filterBy}
              value={filterBy}
              clearable
              onChange={(value) => seFilterBy(value || "")}
            />
          </Popover>
        </div>
      </div>
      <div className="w-full h-[calc(100vh-120px)] overflow-hidden">
        <KanbanBoard tickets={items} loading={isLoading} />
      </div>

      <CreateTicketDrawer
        opened={createTicketDrawerOpened}
        onClose={closeCreateTicketDrawer}
        itemIds={[]}
        onSuccess={() => {
          closeCreateTicketDrawer();
          void queryClient.invalidateQueries({ queryKey: ["items"] });
        }}
      />
    </div>
  );
};

export default TicketsPage;
